import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import type { AppDispatch, RootState } from "../../../../Redux/Store";
import {
  createTemplate, deleteLetter, deleteTemplate, getLetters, getTemplateById,
  getLetterById, getTemplates, updateTemplate, createLetter, updateLetter, getIssuedLogs,
  sendLetterforSignature
} from "../../../../Redux/Reducers/letterHeadReducer.ts";
import { sendSigningLinkAPIHelper } from "../../../../Redux/helpers/letterheadAPI.ts";

// Extracted Helpers
import { INITIAL_TEMPLATE_STATE, INITIAL_LETTER_STATE, generateShortId } from "../types/letterhead.types.ts";
import { parseTemplateToState, parseLetterToState } from "./letterheadMappers.ts";
import { buildTemplatePayload } from "../services/buildTemplatePayload.ts";
import { buildLetterPayload } from "../services/buildLetterPayload.ts";
import { generateAndSendPDF } from "../services/pdfGenerator.ts";
import { useUserDetailContext } from "../../../../Components/UserDetailContext";

export const useLetterhead = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const applicant = location.state?.applicant;
  const applicationId = location.state?.applicationId;

  const BACKEND_URL = process.env.REACT_APP_ENTERPRISE_URL || "http://localhost:4040";
  const ProfessionalExperience = useSelector((state: any) => state?.ProfileArray?.experienceData?.data) || [];
  const selectedCompany = useSelector((state: any) => state?.jobs?.selectedCompany) || {};


  const matchingCompany = ProfessionalExperience?.find(
    (exp: any) => exp?.company_name?.toLowerCase() === selectedCompany?.company_name?.toLowerCase()
  );
  console.log("ProfessionalExperienceMMMMMMMMM", ProfessionalExperience, matchingCompany, selectedCompany)
  // cid from the matched company (recruitment flow when selectedCompany is set)
  const matchedCid = matchingCompany?.companyDetails?.data?.[matchingCompany?.companyDetails?.data?.length - 1]?.cid || "";
  console.log("matchedCidmatchedCid", matchedCid)
  // Fallback: use the first available experience's cid (for individual/sidebar flow)
  const fallbackCid = useMemo(() => {
    if (matchedCid) return matchedCid;
    if (Array.isArray(ProfessionalExperience)) {
      for (const exp of ProfessionalExperience) {
        const data = exp?.companyDetails?.data;
        if (Array.isArray(data) && data.length > 0) {
          const c = data[data.length - 1]?.cid;
          if (c) return c;
        }
      }
    }
    return "";
  }, [matchedCid, ProfessionalExperience]);

  // In recruitment flow: use matched company cid. In individual flow: use fallback cid
  const cid = (location.state?.applicant || location.state?.applicationId) ? matchedCid : (fallbackCid);

  // console.log("Company Name in useLetterhead:", companyName);
  const { userDetails } = useUserDetailContext();


  const userRefId = userDetails?.reference_id;

  // payloadCid: for letter/template payload company_id field
  // In recruitment flow → company cid; In individual flow → userRefId or fallback cid
  const payloadCid = useMemo(() => {
    if (location.state?.applicant?.application_id) {
      return matchedCid;
    }
    return userRefId || fallbackCid;
  }, [location.state, matchedCid, fallbackCid, userRefId]);
  console.log("userDetailsMMMMMMMM", location.state?.applicant)
  console.log("payloadCidMMMMMM", payloadCid, "matchedCid", matchedCid )
  // console.log("cidcidcid", cid)
  const isEditMode = Boolean(id);
  const isComposeLetterPage = location.pathname.includes('/compose-letter');
  const isCreateTemplatePage = location.pathname.includes('/create-template') || (Boolean(id) && !isComposeLetterPage);

  const { templates, letters, loading, error, template, letter, issuedLogs } = useSelector((state: RootState) => state.letterHead);
  console.log("lettersletters", letters)
  // Remove useCompany hook - will need to get company data from elsewhere or make optional

  // Read from location state, then sessionStorage, then default to "templates"
  const [activeTab, setActiveTab] = useState<"templates" | "letters">(() => {
    return location.state?.activeTab ||
      (sessionStorage.getItem("letterheadActiveTab") as "templates" | "letters") ||
      "templates";
  });

  const [selectedLetter, setSelectedLetter] = useState<any>(null);
  const [templateState, setTemplateState] = useState(INITIAL_TEMPLATE_STATE);
  const [letterState, setLetterState] = useState(() => ({
    ...INITIAL_LETTER_STATE,
    iqCode: generateShortId()
  }));
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isESignModalOpen, setIsESignModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);


  // Save the active tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("letterheadActiveTab", activeTab);
  }, [activeTab]);

  // Fetch initial Lists - always use company cid for templates
  useEffect(() => {
    dispatch(getTemplates(payloadCid));
    dispatch(getLetters(payloadCid));
    dispatch(getIssuedLogs());
  }, [dispatch, payloadCid]);

  // Handle Templates/Letters Loading (Dispatch Actions)
  useEffect(() => {
    if (isEditMode && id) {
      if (isCreateTemplatePage) dispatch(getTemplateById(id));
      if (isComposeLetterPage) dispatch(getLetterById(id));
    } else if (isCreateTemplatePage && !isEditMode) {
      // Auto-fill new template defaults - removed company/address references
      setTemplateState({
        ...INITIAL_TEMPLATE_STATE,
        companyName: "", companyEmail: "", companyPhone: "",
        companyAddress: "", cin: "", gst: "", logoUrl: ""
      });
    }
  }, [isEditMode, id, dispatch, isCreateTemplatePage, isComposeLetterPage]);

  // Populate TEMPLATE Form
  useEffect(() => {
    if (isEditMode && template && isCreateTemplatePage) setTemplateState(parseTemplateToState(template, BACKEND_URL));
  }, [template, isEditMode, isCreateTemplatePage, BACKEND_URL]);

  // Populate LETTER Form with Strict ID Check
  useEffect(() => {
    if (isEditMode && letter && isComposeLetterPage && String(letter.letter_id) === String(id)) {
      setLetterState(parseLetterToState(letter, BACKEND_URL));
    }
  }, [letter, isEditMode, isComposeLetterPage, BACKEND_URL, id]);

  // Auto-select template for NEW letters
  useEffect(() => {
    if (isComposeLetterPage && !isEditMode && !letterState.templateId) {
      if (location.state?.templateId) {
        updateLetterState("templateId", location.state.templateId);
      } else if (template?.template_id) {
        updateLetterState("templateId", template.template_id);
      } else if (templates && templates.length > 0) {
        updateLetterState("templateId", templates[0].template_id);
      }
    }
  }, [templates, location.state, template, isComposeLetterPage, isEditMode, letterState.templateId]);

  // Format Template Memo
  const rawTemplate = Array.isArray(templates) ? templates.find((t: any) => String(t.template_id) === String(letterState.templateId)) : null;

  const processedTemplate = useMemo(() => {
    if (!rawTemplate) return null;
    const clone = JSON.parse(JSON.stringify(rawTemplate));
    const header = (clone.TemplateComponents || clone.components || []).find((c: any) => c.component_type === 'header' || c.type === 'header');
    if (header) {
      const props = Array.isArray(header.TemplateProperties) ? header.TemplateProperties.reduce((acc: any, curr: any) => { acc[curr.property_key] = curr.property_value; return acc; }, {}) : header.TemplateProperties;
      header.properties = props;
      try { props.visibleFields = JSON.parse(props.visibleFields); } catch { props.visibleFields = {}; }
    }
    return clone;
  }, [rawTemplate]);

  // Sync QR
  useEffect(() => {
    if (processedTemplate && !isEditMode) {
      const footerProps = processedTemplate.TemplateComponents?.find((c: any) => c.component_type === 'footer' || c.type === 'footer')?.TemplateProperties || {};

      setLetterState(prev => ({ ...prev, enableQR: String(footerProps.enableQR) === "true", iqCode: prev.iqCode || generateShortId() }));
    }
  }, [processedTemplate?.template_id, isEditMode]);

  // Action Handlers
  const updateLetterState = (key: string, value: any) => setLetterState((prev) => ({ ...prev, [key]: value }));
  const handleNavigate = (path: string) => navigate(path);
  const handleEditTemplate = (tid: number) => navigate(`/letterheads/${tid}`);
  const handleEditLetter = (lid: number) => navigate(`/compose-letter/${lid}`);

  const handleESignSuccess = (meta: any) => {
    setLetterState(prev => ({ ...prev, esignVerified: true, esignSignerName: meta.signerName, esignProvider: meta.provider, esignDate: meta.signedAt, esignReference: meta.reference }));
    setIsESignModalOpen(false);
  };

  const handleSaveTemplate = async () => {
    const isRecruitmentFlow = !!(location.state?.applicant || location.state?.applicationId);
    const idType = isRecruitmentFlow ? 'company_id' : 'user_id';
    const formData = buildTemplatePayload(templateState, payloadCid, idType);

    const result = isEditMode && id
      ? await dispatch(updateTemplate({ cid: payloadCid, id, data: formData }))
      : await dispatch(createTemplate(formData));

    if (createTemplate.fulfilled.match(result) || updateTemplate.fulfilled.match(result)) {
      toast.success(isEditMode ? "Template updated successfully!" : "Template created successfully!");
      navigate("/letterheads", { state: { activeTab: 'templates' } });
    } else {
      toast.error("Failed to save template.");
      throw result.payload || result.error || new Error("Duplicate");
    }
  };

  const handleSaveLetter = async (actionType = 'SAVE') => {
    // Remove cid check
    if (actionType === 'SEND_LINK' && letterState.signatories.some(sig => !sig.name || !sig.email)) {
      return toast.warning("Please provide Name and Email for all signatories.");
    }

    try {
      const isRecruitmentFlow = !!(location.state?.applicant || location.state?.applicationId);
      const idType = isRecruitmentFlow ? 'company_id' : 'user_id';
      const { formData, finalLetterId } = buildLetterPayload(letterState, payloadCid, actionType, id, processedTemplate, idType);
      const result = isEditMode ? await dispatch(updateLetter({ id, data: formData })) : await dispatch(createLetter(formData));

      if (createLetter.fulfilled.match(result) || updateLetter.fulfilled.match(result)) {
        if (actionType === 'SEND_FINAL') {
          setIsSendingEmail(true);
          try {
            await generateAndSendPDF(finalLetterId, "Our Company", BACKEND_URL);
            updateLetterState("status", "SENT");
            toast.success(`Letter successfully sent to ${letterState.recipientEmail}`);
            navigate("/letterheads", { state: { activeTab: 'letters' } });
          } catch (err: any) {
            toast.error(`Letter saved as Draft, but email failed: ${err.message}`);
            if (location.state?.applicant) {
              navigate(`/candidateSingle-page/${finalLetterId}`, { replace: true });
            } else {
              navigate("/letterheads", { state: { activeTab: 'letters' }, replace: true });
            }
          } finally { setIsSendingEmail(false); }

        } else if (actionType === 'SEND_LINK') {
          setIsSendingEmail(true);
          try {
            // await dispatch(sendLetterforSignature(finalLetterId, { companyName: "Our Company", frontendUrl: window.location.origin, subject: letterState.subject }) )
            // await sendSigningLinkAPIHelper(finalLetterId, { companyName: "Our Company", frontendUrl: window.location.origin, subject: letterState.subject });
            await sendSigningLinkAPIHelper(finalLetterId, { companyName: companyName, frontendUrl: process.env.REACT_APP_ENTERPRISE_URL, subject: `Offer Letter for ${applicant.role} position` });
            updateLetterState("status", "PENDING_SIGNATURE");

            console.log("API CALLED I THINK")
            toast.success(`Secure signing links successfully sent!`);
            if (location.state?.applicant) {
              navigate(`/candidateSingle-page/${applicant?.application_id}`, { state: { activeTab: 'letters' } });
            } else {
              navigate("/letterheads", { state: { activeTab: 'letters' } });
            }
          } catch (err) {
            toast.error(`Letter saved as Draft, but links failed to send.`);
            if (location.state?.applicant) {
              navigate(`/candidateSingle-page/${applicant?.application_id}`, { replace: true });
            } else {
              navigate("/letterheads", { state: { activeTab: 'letters' }, replace: true });
            }
          } finally { setIsSendingEmail(false); }

        } else {
          toast.success(isEditMode ? "Letter updated successfully!" : "Letter saved as Draft!");
          if (location.state?.applicant) {
            navigate(`/candidateSingle-page/${applicant?.application_id}`, { state: { activeTab: 'letters' } });
          } else {
            navigate("/letterheads", { state: { activeTab: 'letters' } });
          }
        }
      } else {
        toast.error("Failed to save letter.");
      }
    } catch (err) {
      toast.error("An unexpected error occurred.");
    }
  };

  return {
    getTemplates,
    getLetters,
    isLoading: loading, isError: error, isEditMode, activeTab, templates, letters, selectedLetter,
    companyProfile: null, // Remove company profile reference
    letterId: id,
    iquiqueCode: (isEditMode && letter && String(letter.letter_id) === String(id)) ? letter.iquique_code : letterState.iqCode,
    companyDocuments: null, // Remove company documents reference
    registeredAddress: null, // Remove registered address reference
    templateState, setTemplateState, handleSave: handleSaveTemplate,
    letterState, updateLetterState, processedTemplate, isSignatureModalOpen, setIsSignatureModalOpen,
    isESignModalOpen, setIsESignModalOpen, handleSaveLetter, handleESignSuccess, setLetterState,
    setActiveTab, setSelectedLetter, handleNavigate, handleEditTemplate, handleEditLetter, handleViewLetter: setSelectedLetter,
    handleCloseModal: () => setSelectedLetter(null),
    handleDeleteLetter: async (lid: number) => {
      const result = await dispatch(deleteLetter(lid.toString()));
      if (deleteLetter.fulfilled.match(result)) {
        toast.success("Letter deleted successfully!");
        dispatch(getLetters(payloadCid));
      } else {
        toast.error("Failed to delete letter.");
      }
    },
    handleDeleteTemplate: async (tid: number) => {
      const result = await dispatch(deleteTemplate(tid));
      if (deleteTemplate.fulfilled.match(result)) {
        toast.success("Template deleted successfully!");
        dispatch(getLetters(payloadCid));
      } else {
        toast.error("Failed to delete template.");
      }
    },
    issuedLogs, isSendingEmail
  };
};




// src/Components/Dashboard/letterhead/hooks/useLetterhead.ts
// import { useEffect, useState, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate, useParams, useLocation } from "react-router-dom";
// import { toast } from "react-toastify";
// import type { AppDispatch, RootState } from "../../../../Redux/Store";
// import {
//   createTemplate, deleteLetter, deleteTemplate, getLetters, getTemplateById,
//   getLetterById, getTemplates, updateTemplate, createLetter, updateLetter, getIssuedLogs,
//   sendLetterforSignature
// } from "../../../../Redux/Reducers/letterHeadReducer.ts";
// import { sendSigningLinkAPIHelper } from "../../../../Redux/helpers/letterheadAPI.ts";

// // Extracted Helpers
// import { INITIAL_TEMPLATE_STATE, INITIAL_LETTER_STATE, generateShortId } from "../types/letterhead.types.ts";
// import { parseTemplateToState, parseLetterToState } from "./letterheadMappers.ts";
// import { buildTemplatePayload } from "../services/buildTemplatePayload.ts";
// import { buildLetterPayload } from "../services/buildLetterPayload.ts";
// import { generateAndSendPDF } from "../services/pdfGenerator.ts";
// import { useUserDetailContext } from "../../../../Components/UserDetailContext";

// export const useLetterhead = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const dispatch = useDispatch();
//   const { id } = useParams<{ id: string }>();
//   const applicant = location.state?.applicant;
//   const applicationId = location.state?.applicationId;

//   const BACKEND_URL = process.env.REACT_APP_ENTERPRISE_URL || "http://localhost:4040";
//   const ProfessionalExperience = useSelector((state: any) => state?.ProfileArray?.experienceData?.data) || [];
//   const selectedCompany = useSelector((state: any) => state?.jobs?.selectedCompany) || {};


//   console.log("ProfessionalExperienceMMMM", ProfessionalExperience, selectedCompany)

//   const matchingCompany = ProfessionalExperience?.find(
//     (exp: any) => exp?.company_name?.toLowerCase() === selectedCompany?.company_name?.toLowerCase()
//   );
//   // console.log("ProfessionalExperienceMMMMMMMMM", ProfessionalExperience, matchingCompany, selectedCompany)
//   // cid from the matched company (recruitment flow when selectedCompany is set)
//   const matchedCid = matchingCompany?.companyDetails?.data?.[matchingCompany?.companyDetails?.data?.length - 1]?.cid || "";

//   // Fallback: use the first available experience's cid (for individual/sidebar flow)
//   const fallbackCid = useMemo(() => {
//     if (matchedCid) return matchedCid;
//     if (Array.isArray(ProfessionalExperience)) {
//       for (const exp of ProfessionalExperience) {
//         const data = exp?.companyDetails?.data;
//         if (Array.isArray(data) && data.length > 0) {
//           const c = data[data.length - 1]?.cid;
//           if (c) return c;
//         }
//       }
//     }
//     return "";
//   }, [matchedCid, ProfessionalExperience]);

//   // In recruitment flow: use matched company cid. In individual flow: use fallback cid
//   const cid = (location.state?.applicant || location.state?.applicationId) ? matchedCid : (fallbackCid);

//   // console.log("Company Name in useLetterhead:", companyName);
//   const { userDetails } = useUserDetailContext();


//   const userRefId = userDetails?.reference_id;

//   // payloadCid: for letter/template payload company_id field
//   // In recruitment flow → company cid; In individual flow → userRefId or fallback cid
//   const payloadCid = useMemo(() => {
//     if (location.state?.applicant?.application_id) {
//       return matchedCid;
//     }
//     return userRefId || fallbackCid;
//   }, [location.state, matchedCid, fallbackCid, userRefId]);

//   // console.log("userDetailsMMMMMMMM", location.state?.applicant)
//   // console.log("payloadCidMMMMMM", payloadCid, "matchedCid", matchedCid)

//   const isEditMode = Boolean(id);
//   const isComposeLetterPage = location.pathname.includes('/compose-letter');
//   const isCreateTemplatePage = location.pathname.includes('/create-template') || (Boolean(id) && !isComposeLetterPage);

//   const { templates, letters, loading, error, template, letter, issuedLogs } = useSelector((state: RootState) => state.letterHead);
//   // console.log("lettersletters", letters)

//   // Read from location state, then sessionStorage, then default to "templates"
//   const [activeTab, setActiveTab] = useState<"templates" | "letters">(() => {
//     return location.state?.activeTab ||
//       (sessionStorage.getItem("letterheadActiveTab") as "templates" | "letters") ||
//       "templates";
//   });

//   const [selectedLetter, setSelectedLetter] = useState<any>(null);

//   // 🔥 FIX: Ensure letterState is properly initialized with default signatory
//   const [templateState, setTemplateState] = useState(INITIAL_TEMPLATE_STATE);
//   const [letterState, setLetterState] = useState(() => ({
//     ...INITIAL_LETTER_STATE,
//     iqCode: generateShortId(),
//     // Initialize signatories with a default entry for send_link

//     signatories: [
//       {
//         id: generateShortId(),
//         role: 'Signatory',
//         name: '',
//         email: '',
//         status: 'PENDING'
//       }
//     ],
//     signatureType: 'send_link' // Default to send_link
//   }));


//   // console.log("letterState8564589522", letterState)
//   const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
//   const [isESignModalOpen, setIsESignModalOpen] = useState(false);
//   const [isSendingEmail, setIsSendingEmail] = useState(false);


//   // Save the active tab to sessionStorage whenever it changes
//   useEffect(() => {
//     sessionStorage.setItem("letterheadActiveTab", activeTab);
//   }, [activeTab]);

//   // Fetch initial Lists - always use company cid for templates
//   useEffect(() => {
//     dispatch(getTemplates(payloadCid));
//     dispatch(getLetters(payloadCid));
//     dispatch(getIssuedLogs());
//   }, [dispatch, payloadCid]);

//   // Handle Templates/Letters Loading (Dispatch Actions)
//   useEffect(() => {
//     if (isEditMode && id) {
//       if (isCreateTemplatePage) dispatch(getTemplateById(id));
//       if (isComposeLetterPage) dispatch(getLetterById(id));
//     } else if (isCreateTemplatePage && !isEditMode) {
//       // Auto-fill new template defaults - removed company/address references
//       setTemplateState({
//         ...INITIAL_TEMPLATE_STATE,
//         companyName: "", companyEmail: "", companyPhone: "",
//         companyAddress: "", cin: "", gst: "", logoUrl: ""
//       });
//     }
//   }, [isEditMode, id, dispatch, isCreateTemplatePage, isComposeLetterPage]);

//   // Populate TEMPLATE Form
//   useEffect(() => {
//     if (isEditMode && template && isCreateTemplatePage) setTemplateState(parseTemplateToState(template, BACKEND_URL));
//   }, [template, isEditMode, isCreateTemplatePage, BACKEND_URL]);

//   // Populate LETTER Form with Strict ID Check
//   useEffect(() => {
//     if (isEditMode && letter && isComposeLetterPage && String(letter.letter_id) === String(id)) {
//       const parsedState = parseLetterToState(letter, BACKEND_URL);
//       setLetterState(parsedState);
//     }
//   }, [letter, isEditMode, isComposeLetterPage, BACKEND_URL, id]);

//   // Auto-select template for NEW letters
//   useEffect(() => {
//     if (isComposeLetterPage && !isEditMode && !letterState.templateId) {
//       if (location.state?.templateId) {
//         updateLetterState("templateId", location.state.templateId);
//       } else if (template?.template_id) {
//         updateLetterState("templateId", template.template_id);
//       } else if (templates && templates.length > 0) {
//         updateLetterState("templateId", templates[0].template_id);
//       }
//     }
//   }, [templates, location.state, template, isComposeLetterPage, isEditMode, letterState.templateId]);

//   // Format Template Memo
//   const rawTemplate = Array.isArray(templates) ? templates.find((t: any) => String(t.template_id) === String(letterState.templateId)) : null;

//   const processedTemplate = useMemo(() => {
//     if (!rawTemplate) return null;
//     const clone = JSON.parse(JSON.stringify(rawTemplate));
//     const header = (clone.TemplateComponents || clone.components || []).find((c: any) => c.component_type === 'header' || c.type === 'header');
//     if (header) {
//       const props = Array.isArray(header.TemplateProperties) ? header.TemplateProperties.reduce((acc: any, curr: any) => { acc[curr.property_key] = curr.property_value; return acc; }, {}) : header.TemplateProperties;
//       header.properties = props;
//       try { props.visibleFields = JSON.parse(props.visibleFields); } catch { props.visibleFields = {}; }
//     }
//     return clone;
//   }, [rawTemplate]);

//   // Sync QR
//   useEffect(() => {
//     if (processedTemplate && !isEditMode) {
//       const footerProps = processedTemplate.TemplateComponents?.find((c: any) => c.component_type === 'footer' || c.type === 'footer')?.TemplateProperties || {};

//       setLetterState(prev => ({ ...prev, enableQR: String(footerProps.enableQR) === "true", iqCode: prev.iqCode || generateShortId() }));
//     }
//   }, [processedTemplate?.template_id, isEditMode]);

//   // 🔥 FIX: Action Handlers - Make sure updateLetterState works correctly
//   // const updateLetterState = (key: string, value: any) => {
//   //   console.log(`🔄 Updating letterState: ${key} =`, value);
//   //   setLetterState((prev) => {
//   //     const updated = { ...prev, [key]: value };
//   //     console.log('📝 Updated letterState:', updated);
//   //     return updated;
//   //   });
//   // };
//   const updateLetterState = (key: string, value: any) => {
//     // console.log(`🔄 Updating letterState: ${key} =`, value);
//     setLetterState((prev) => {
//       // Handle nested updates for signatories
//       if (key === 'signatories') {
//         return {
//           ...prev,
//           signatories: Array.isArray(value) ? value : [value]
//         };
//       }
//       return { ...prev, [key]: value };
//     });
//   };



//   const handleNavigate = (path: string) => navigate(path);
//   const handleEditTemplate = (tid: number) => navigate(`/letterheads/${tid}`);
//   const handleEditLetter = (lid: number) => navigate(`/compose-letter/${lid}`);

//   const handleESignSuccess = (meta: any) => {
//     setLetterState(prev => ({ ...prev, esignVerified: true, esignSignerName: meta.signerName, esignProvider: meta.provider, esignDate: meta.signedAt, esignReference: meta.reference }));
//     setIsESignModalOpen(false);
//   };

//   const handleSaveTemplate = async () => {
//     const isRecruitmentFlow = !!(location.state?.applicant || location.state?.applicationId);
//     const idType = isRecruitmentFlow ? 'company_id' : 'user_id';
//     const formData = buildTemplatePayload(templateState, payloadCid, idType);

//     const result = isEditMode && id
//       ? await dispatch(updateTemplate({ cid: payloadCid, id, data: formData }))
//       : await dispatch(createTemplate(formData));

//     if (createTemplate.fulfilled.match(result) || updateTemplate.fulfilled.match(result)) {
//       toast.success(isEditMode ? "Template updated successfully!" : "Template created successfully!");
//       navigate("/letterheads", { state: { activeTab: 'templates' } });
//     } else {
//       toast.error("Failed to save template.");
//       throw result.payload || result.error || new Error("Duplicate");
//     }
//   };

//   const handleSaveLetter = async (actionType = 'SAVE') => {
//     // 🔥 Debug: Log current letter state
//     // console.log('📝 Current Letter State before save:', letterState);
//     // console.log('📝 Recipient Name in state:', letterState.recipientName);
//     // console.log('📝 Recipient Email in state:', letterState.recipientEmail);
//     // console.log('📝 Signatories in state:', letterState.signatories);

//     if (actionType === 'SEND_LINK' && letterState.signatories.some(sig => !sig.name || !sig.email)) {
//       return toast.warning("Please provide Name and Email for all signatories.");
//     }

//     try {
//       const isRecruitmentFlow = !!(location.state?.applicant || location.state?.applicationId);
//       const idType = isRecruitmentFlow ? 'company_id' : 'user_id';
//       // console.log("IAMCHECKING")
//       const finalLtterState = {
//         ...letterState,
//         recipientName: letterState?.recipientName,
//         recipientEmail: letterState?.recipientEmail

//       }
//       // console.log("finalLtterStateMMMMMMMm", finalLtterState)
//       const { formData, finalLetterId } = buildLetterPayload(finalLtterState, payloadCid, actionType, id, processedTemplate, idType);
//       const result = isEditMode ? await dispatch(updateLetter({ id, data: formData })) : await dispatch(createLetter(formData));

//       if (createLetter.fulfilled.match(result) || updateLetter.fulfilled.match(result)) {
//         if (actionType === 'SEND_FINAL') {
//           setIsSendingEmail(true);
//           try {
//             await generateAndSendPDF(finalLetterId, "Our Company", BACKEND_URL, letterState.recipientName, letterState.recipientEmail, letterState.recipientAddress);
//             updateLetterState("status", "SENT");
//             toast.success(`Letter successfully sent to ${letterState.recipientEmail}`);
//             navigate("/letterheads", { state: { activeTab: 'letters' } });
//           } catch (err: any) {
//             toast.error(`Letter saved as Draft, but email failed: ${err.message}`);
//             if (location.state?.applicant) {
//               navigate(`/candidateSingle-page/${finalLetterId}`, { replace: true });
//             } else {
//               navigate("/letterheads", { state: { activeTab: 'letters' }, replace: true });
//             }
//           } finally { setIsSendingEmail(false); }

//         } else if (actionType === 'SEND_LINK') {
//           setIsSendingEmail(true);
//           try {
//             // Get company name from state or use fallback
//             const companyName = letterState.companyName || "Our Company";
//             await sendSigningLinkAPIHelper(finalLetterId, {
//               companyName: companyName,
//               frontendUrl: process.env.REACT_APP_ENTERPRISE_URL,
//               subject: `Offer Letter for ${applicant?.role || 'position'}`
//             });
//             updateLetterState("status", "PENDING_SIGNATURE");

//             // console.log("API CALLED I THINK")
//             toast.success(`Secure signing links successfully sent!`);
//             if (location.state?.applicant) {
//               navigate(`/candidateSingle-page/${applicant?.application_id}`, { state: { activeTab: 'letters' } });
//             } else {
//               navigate("/letterheads", { state: { activeTab: 'letters' } });
//             }
//           } catch (err) {
//             toast.error(`Letter saved as Draft, but links failed to send.`);
//             if (location.state?.applicant) {
//               navigate(`/candidateSingle-page/${applicant?.application_id}`, { replace: true });
//             } else {
//               navigate("/letterheads", { state: { activeTab: 'letters' }, replace: true });
//             }
//           } finally { setIsSendingEmail(false); }

//         } else {
//           toast.success(isEditMode ? "Letter updated successfully!" : "Letter saved as Draft!");
//           if (location.state?.applicant) {
//             navigate(`/candidateSingle-page/${applicant?.application_id}`, { state: { activeTab: 'letters' } });
//           } else {
//             navigate("/letterheads", { state: { activeTab: 'letters' } });
//           }
//         }
//       } else {
//         toast.error("Failed to save letter.");
//       }
//     } catch (err) {
//       toast.error("An unexpected error occurred.");
//     }
//   };

//   return {
//     getTemplates,
//     getLetters,
//     isLoading: loading, isError: error, isEditMode, activeTab, templates, letters, selectedLetter,
//     companyProfile: null,
//     letterId: id,
//     iquiqueCode: (isEditMode && letter && String(letter.letter_id) === String(id)) ? letter.iquique_code : letterState.iqCode,
//     companyDocuments: null,
//     registeredAddress: null,
//     templateState, setTemplateState, handleSave: handleSaveTemplate,
//     letterState, updateLetterState, processedTemplate, isSignatureModalOpen, setIsSignatureModalOpen,
//     isESignModalOpen, setIsESignModalOpen, handleSaveLetter, handleESignSuccess, setLetterState,
//     setActiveTab, setSelectedLetter, handleNavigate, handleEditTemplate, handleEditLetter, handleViewLetter: setSelectedLetter,
//     handleCloseModal: () => setSelectedLetter(null),
//     handleDeleteLetter: async (lid: number) => {
//       const result = await dispatch(deleteLetter(lid.toString()));
//       if (deleteLetter.fulfilled.match(result)) {
//         toast.success("Letter deleted successfully!");
//         dispatch(getLetters(payloadCid));
//       } else {
//         toast.error("Failed to delete letter.");
//       }
//     },
//     handleDeleteTemplate: async (tid: number) => {
//       const result = await dispatch(deleteTemplate(tid));
//       if (deleteTemplate.fulfilled.match(result)) {
//         toast.success("Template deleted successfully!");
//         dispatch(getLetters(payloadCid));
//       } else {
//         toast.error("Failed to delete template.");
//       }
//     },
//     issuedLogs, isSendingEmail
//   };
// };